import { Card, H4, Option, Stack, Title } from "@introist/react-foundation/v2";
import { AttributeIcon } from "../../../components/atoms";
import { DataSourceMappingCard } from "./DataMappingCard";
import { OrderableList } from "../../../foundation";
import { ShortUuid } from "../../../utils/Utils";
import { SetupCard } from "../../triggers/event-triggers/editors/SetupCard";
import { ReactNode } from "react";
import { z } from "zod";

export const DataMappingSchema = z
  .object({
    id: z.string(),
    sourceField: z.string().nullish(),
    targetField: z.string(),
    transformFn: z.string().nullish(),
    identifier: z.boolean().nullish(),
    additional: z.string().nullish()
  })
  .refine(
    val => {
      return val.sourceField || val.transformFn;
    },
    {
      message: "Either source field or transform must be provided"
    }
  );
export type DataMapping = z.infer<typeof DataMappingSchema>;

export type DataMappingField = {
  key: string;
  title: string;
  additionalOptions?: Option[];
  additionalDescription?: string;
};

export const DataMappingEditor = ({
  value,
  onChange,
  sourceFields,
  sourceSelector,
  targetFields,
  blended,
  placeholder,
  sourceHeader,
  targetHeader
}: {
  value: DataMapping[];
  onChange: (value: DataMapping[]) => unknown;
  sourceSelector?: (onSelected: (field: string) => unknown, value?: string | null) => JSX.Element;
  sourceFields?: DataMappingField[];
  targetFields?: DataMappingField[];
  blended?: boolean;
  placeholder?: string | null;
  sourceHeader?: ReactNode;
  targetHeader?: ReactNode;
}) => {
  const editor = (
    <Stack vertical>
      {(sourceHeader || targetHeader) && (
        <Stack
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            borderBottom: "1px solid var(--palette-border-subdued)",
            padding: "var(--spacing-medium) 0"
          }}
        >
          <div style={{ marginLeft: "10px" }}>{sourceHeader}</div>
          {targetHeader}
        </Stack>
      )}
      {value.length > 0 && (
        <OrderableList
          data={value}
          onReorder={items => onChange(items)}
          renderRow={mapping => (
            <DataSourceMappingCard
              value={mapping}
              sourceSelector={sourceSelector}
              sourceFields={sourceFields}
              targetFields={targetFields}
              onChange={mapping => onChange(value.map(m => (m.id === mapping.id ? mapping : m)))}
              onRemove={() => onChange(value.filter(m => m.id !== mapping.id))}
              blended={blended}
            />
          )}
        />
      )}
      {value.length === 0 && placeholder !== null && (
        <Title style={{ alignSelf: "center", margin: "1rem 0" }}>
          {placeholder ?? "No fields mapped"}
        </Title>
      )}
      <SetupCard
        icon="arrowsLeftRight"
        title="Add mapping"
        style={{ padding: "var(--spacing-medium)", marginTop: "var(--spacing-medium)" }}
        onClick={() =>
          onChange([...value, { id: ShortUuid.generate(), sourceField: "", targetField: "" }])
        }
      />
    </Stack>
  );

  if (blended) return editor;

  return (
    <Card>
      <Stack vertical>
        <Stack vertical gap="small">
          <Stack gap="small">
            <AttributeIcon ghosted />
            <H4>Data mapping</H4>
          </Stack>
        </Stack>
        {editor}
      </Stack>
    </Card>
  );
};
